import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { EmptyState, PriorityBadge, StatCard, TaskStatusBadge, DemoBadge, OPP_TYPE_LABELS, SeverityBadge } from "@/components/shared";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { Globe, Loader2, Play, RefreshCw, Search, Target } from "lucide-react";
import { toast } from "sonner";

export default function Overview() {
  const { activeWebsite } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditing, setAuditing] = useState(false);

  const load = async () => {
    if (!activeWebsite) { setLoading(false); return; }
    try {
      const { data: d } = await api.get(`/websites/${activeWebsite.id}/overview`);
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { setLoading(true); load(); }, [activeWebsite?.id]); // eslint-disable-line

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data: job } = await api.post(`/websites/${activeWebsite.id}/audit`);
      toast.success("Crawl & audit started");
      const poll = setInterval(async () => {
        const { data: j } = await api.get(`/jobs/${job.id}`);
        if (j.status === "completed") { clearInterval(poll); setAuditing(false); toast.success("Audit complete"); load(); }
        if (j.status === "failed") { clearInterval(poll); setAuditing(false); toast.error(j.error || "Audit failed"); }
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); setAuditing(false); }
  };

  if (!activeWebsite) {
    return (
      <EmptyState icon={Globe} title="No website yet" message="Add your first website to start analysing SEO." testId="overview-no-website">
        <Button onClick={() => navigate("/onboarding?step=website")} data-testid="overview-add-website-button">Add website</Button>
      </EmptyState>
    );
  }

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  }

  const k = data?.kpis || {};
  const hasSearchData = (data?.daily || []).length > 0;
  const isDemo = data?.data_source === "demo";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Overview</h1>
          <p className="text-xs text-muted-foreground">
            {activeWebsite.domain}
            {data?.last_crawl_at ? ` · last crawl ${new Date(data.last_crawl_at).toLocaleString()}` : " · not crawled yet"}
            {isDemo && <span className="ml-2"><DemoBadge /></span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="overview-refresh-button"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</Button>
          <Button size="sm" onClick={runAudit} disabled={auditing} data-testid="overview-run-audit-button">
            {auditing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
            {auditing ? "Crawling…" : "Run audit"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Organic clicks (28d)" value={k.clicks?.toLocaleString()} delta={k.clicks - k.clicks_prev} testId="kpi-organic-clicks-value" />
        <StatCard label="Impressions (28d)" value={k.impressions?.toLocaleString()} delta={k.impressions - k.impressions_prev} testId="kpi-impressions-value" />
        <StatCard label="Avg position" value={k.avg_position ?? "—"} delta={k.avg_position && k.avg_position_prev ? k.avg_position - k.avg_position_prev : null} deltaGoodWhen="down" testId="kpi-avg-position-value" />
        <StatCard label="Organic conversions" value={k.conversions?.toLocaleString()} sub={k.conversion_rate != null ? `${(k.conversion_rate * 100).toFixed(1)}% rate` : ""} testId="kpi-conversions-value" />
      </div>

      <div className="grid gap-4 xl:grid-cols-12">
        <div className="space-y-4 xl:col-span-8">
          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Clicks & impressions {isDemo && <DemoBadge />}</CardTitle></CardHeader>
            <CardContent>
              {hasSearchData ? (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.daily} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke="hsl(214 18% 88%)" strokeOpacity={0.6} vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(215 14% 40%)" }} tickLine={false} axisLine={false} minTickGap={30} />
                      <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "hsl(215 14% 40%)" }} tickLine={false} axisLine={false} width={40} />
                      <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "hsl(215 14% 40%)" }} tickLine={false} axisLine={false} width={46} />
                      <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Area yAxisId="r" type="monotone" dataKey="impressions" stroke="hsl(215 14% 65%)" fill="hsl(215 14% 65% / 0.15)" strokeWidth={1.5} name="Impressions" />
                      <Area yAxisId="l" type="monotone" dataKey="clicks" stroke="hsl(218 66% 36%)" fill="hsl(218 66% 36% / 0.12)" strokeWidth={2} name="Clicks" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState icon={Search} title="No Search Console data yet" message="Connect Google Search Console to see real performance, or load labelled demo data to explore." testId="overview-no-search-data">
                  <Button size="sm" onClick={() => navigate("/dashboard/connections")} data-testid="overview-connect-gsc-button">Connect Search Console</Button>
                  <Button size="sm" variant="outline" onClick={runAudit} disabled={auditing}>Run website audit</Button>
                </EmptyState>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">High-priority opportunities</CardTitle>
                <Button asChild variant="ghost" size="sm" className="h-7 text-xs"><Link to="/dashboard/opportunities" data-testid="overview-view-all-opportunities">View all</Link></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data?.top_opportunities || []).length === 0 && (
                <EmptyState icon={Target} title="No opportunities yet" message="Run an audit and connect Search Console (or load demo data) to generate evidence-based opportunities." />
              )}
              {(data?.top_opportunities || []).map((o) => (
                <button key={o.id} onClick={() => navigate("/dashboard/opportunities")}
                  className="flex w-full items-start justify-between gap-3 rounded-md border p-3 text-left hover:bg-muted/40"
                  data-testid="overview-opportunity-row">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <PriorityBadge priority={o.priority} />
                      <span className="text-xs font-medium text-muted-foreground">{OPP_TYPE_LABELS[o.type] || o.type}</span>
                      {o.is_demo && <DemoBadge />}
                    </div>
                    <div className="mt-1 truncate text-sm font-medium">{o.recommendation?.title}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">{o.page || o.queries?.[0]}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-semibold tabular-nums">{o.score}</div>
                    <div className="text-[10px] text-muted-foreground">score</div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4 xl:col-span-4">
          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Internal SEO Health Score</CardTitle></CardHeader>
            <CardContent>
              {data?.health ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <span data-testid="health-score-value" className="text-4xl font-bold tabular-nums">{data.health.score}</span>
                    <span className="text-sm text-muted-foreground">/100</span>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">Internal score - not a Google metric. Click-through each component:</p>
                  <div className="mt-3 space-y-2">
                    {Object.entries(data.health.components || {}).map(([name, c]) => (
                      <div key={name}>
                        <div className="flex justify-between text-xs">
                          <span className="capitalize text-muted-foreground">{name.replaceAll("_", " ")}</span>
                          <span className="font-medium tabular-nums">{c.score}</span>
                        </div>
                        <Progress value={c.score} className="h-1" />
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <EmptyState title="No health score yet" message="Run a website audit to compute it.">
                  <Button size="sm" onClick={runAudit} disabled={auditing}>Run audit</Button>
                </EmptyState>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Technical alerts</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data?.critical_issues || []).length === 0 && <p className="text-xs text-muted-foreground">No critical or high-severity issues detected.</p>}
              {(data?.critical_issues || []).map((i) => (
                <div key={i.id} className="flex items-start gap-2 rounded-md border p-2">
                  <SeverityBadge severity={i.severity} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium">{i.message}</div>
                    <div className="truncate font-mono text-[10px] text-muted-foreground">{i.url}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent AI tasks</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data?.recent_tasks || []).length === 0 && <p className="text-xs text-muted-foreground">No AI tasks yet. Approve an opportunity to create one.</p>}
              {(data?.recent_tasks || []).map((t) => (
                <Link key={t.id} to={`/dashboard/tasks/${t.id}`} className="flex items-center justify-between gap-2 rounded-md border p-2 hover:bg-muted/40">
                  <span className="truncate text-xs font-medium">{t.title}</span>
                  <TaskStatusBadge status={t.status} />
                </Link>
              ))}
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Pages crawled" value={k.pages_total} testId="kpi-pages-total" />
            <StatCard label="Indexable" value={k.pages_indexable} testId="kpi-pages-indexable" />
          </div>
        </div>
      </div>
    </div>
  );
}
